/**
 * RequirePermission - redirects to "/" unless the user's effective RBAC
 * permissions include resource:action. Use for workspaces gated by an RBAC
 * role (e.g. PRP counselors) rather than the user's primary role.
 * Server-side RBAC remains authoritative; this only hides the UI.
 *
 * Usage:
 *   <Route element={<RequirePermission resource="prp" action="list" />}>
 *     <Route path="prp" element={<PrpDashboard />} />
 *   </Route>
 */
import { Navigate, Outlet } from "react-router-dom";
import { PRP_ENABLED } from "@/lib/hidden-features";
import { useAuth } from "@/hooks/use-auth";
import { usePermissions } from "@/hooks/use-permissions";
import { getAccessToken } from "@/api/client";
import { isPrpAssigned, rolesFromAccessToken } from "@/lib/prp-home";

interface Props {
  resource: string;
  action: string;
}

export function RequirePermission({ resource, action }: Props) {
  const { user } = useAuth();
  const { can, isLoading, isError } = usePermissions();
  const isPrp = resource === "prp";
  const staffPrp = PRP_ENABLED && isPrp && user?.role === "staff";

  if (isLoading && !staffPrp) return null;
  const allowed =
    can(resource, action) ||
    staffPrp ||
    (resource === "prp" && isPrpAssigned(user, rolesFromAccessToken(getAccessToken())));
  // Only redirect on a real answer; if permissions failed to load, let the
  // page render and the server decide.
  // A hidden module stays closed even when permissions fail to load.
  if (!allowed && (!isError || (isPrp && !PRP_ENABLED))) {
    return <Navigate to="/dashboard" replace />;
  }

  return <Outlet />;
}
